import type { GrantCode } from '../../auth/grants'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import DataTable from './DataTable'
import AttachmentDownloadButton from './AttachmentDownloadButton'
import { useGrants } from '../../auth/useCurrentUser'
import { previewKindOf } from '../../lib/attachmentPreview'
import { formatBytes } from '../../lib/fileSize'
import { formatDateTime } from '../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'

/** Tablo satırı: yüklenmiş ek (downloadId dolu) ya da henüz yüklenmemiş yerel dosya (downloadId yok). */
export interface AttachmentTableRow {
  /** Kararlı satır anahtarı (yüklenmişte id; yerelde ör. "ad:boyut"). */
  key: string
  fileName: string
  /** Uzantı ("pdf"); tür sütununda BÜYÜK harf gösterilir. */
  extension: string
  size: number
  /** Sunucudaki ek kimliği — verilirse İndir ikonu gösterilir (yüklenmemiş dosyada yok). */
  downloadId?: number
  /** Yükleyen kullanıcı adı — verilirse "Yükleyen" sütunu gösterilir (yüklenmemiş dosyada yok). */
  uploadedByName?: string | null
  /** Yüklenme zamanı (ISO) — verilirse "Yüklenme" sütunu gösterilir. */
  uploadedAt?: string
}

interface Props {
  rows: AttachmentTableRow[]
  /** Boşken gösterilecek not; verilmezse boşken hiçbir şey render edilmez (yükleyici tek başına yeterli). */
  emptyText?: string
  /** Kaldır (satır işlemi) — verilirse çöp ikonu gösterilir. */
  onRemove?: (row: AttachmentTableRow) => void
  /** İşlem sürüyor → işlem butonları pasif. */
  busy?: boolean
  /**
   * **Görüntüle** (108) — verilirse, tarayıcıda gömülü açılabilen satırlarda göz ikonu çizilir.
   * Çağıran içeriği nerede göstereceğini kendisi bilir (ör. pop-up içinde panel); bu bileşen
   * yalnız hangi satırın önizlenebilir olduğuna karar verir ({@link previewKindOf}) — Office ve
   * arşiv dosyalarında ikon hiç çıkmaz, çünkü tarayıcı onları gömülü açamaz.
   */
  onPreview?: (row: AttachmentTableRow) => void
  /** İndirme için gereken yetki (bağlama göre) — `AttachmentDownloadButton`'a geçirilir. */
  requiredGrant: GrantCode
}

/**
 * Ortak dosya-eki tablosu (Reuse Mandate): <b>Dosya Adı · Tür · Boyut</b> + satır işlemleri (İndir/Sil, ikon-only).
 * Hem yüklenmiş ekler (Toplantı Detay — İndir + Sil) hem yüklenmeyi bekleyen yerel dosyalar (Tutanak Oluştur —
 * yalnız Sil) için kullanılır; İndir ikonu {@link AttachmentTableRow.downloadId} verilen satırlarda çıkar.
 */
export default function AttachmentTable({
  rows,
  emptyText,
  onRemove,
  onPreview,
  busy,
  requiredGrant,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { hasGrant } = useGrants()
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  if (rows.length === 0) {
    return emptyText ? <p className="act-empty-hint">{emptyText}</p> : null
  }

  // İndirme yetkisi yoksa İndir butonu render EDİLMEZ (AttachmentDownloadButton kendi kapısını uygular),
  // bu yüzden kolonun görünürlüğü de yetkiye bakmalı — aksi hâlde ne indirme ne yönetme yetkisi olan
  // kullanıcıda BOŞ bir işlem kolonu kalıyordu (push kapısı code review bulgusu).
  const canDownloadAny = hasGrant(requiredGrant) && rows.some((r) => r.downloadId != null)
  // Görüntüle de aynı kapıya tabidir: `onPreview` verilmiş olması yetmez, satırlarda GERÇEKTEN
  // önizlenebilir bir tür bulunmalı — yoksa yalnız Office/arşiv dosyası olan ve indirme yetkisi
  // bulunmayan bir kullanıcıda kolon yine boş kalırdı (yukarıdaki invaryantın aynısı).
  const canPreviewAny = onPreview != null && rows.some((r) => previewKindOf(r.extension) != null)
  const showActions = onRemove != null || canDownloadAny || canPreviewAny
  const showUploader = rows.some((r) => r.uploadedByName != null)
  const showUploadedAt = rows.some((r) => r.uploadedAt != null)

  if (isMobile) {
    return (
      <AttachmentCards
        rows={rows}
        showActions={showActions}
        showUploader={showUploader}
        showUploadedAt={showUploadedAt}
        onRemove={onRemove}
        onPreview={onPreview}
        busy={busy}
        requiredGrant={requiredGrant}
      />
    )
  }

  return (
    <DataTable value={rows} dataKey="key" paginator={false} className="minutes-participant-table">
      <Column
        header={t('common.fileTable.name')}
        body={(r: AttachmentTableRow) => (
          <span className="minutes-cell-strong act-cell-clip act-cell-clip--wide" title={r.fileName}>
            {r.fileName}
          </span>
        )}
      />
      <Column
        header={t('common.fileTable.type')}
        headerStyle={{ width: '8rem' }}
        body={(r: AttachmentTableRow) => (r.extension || '').toUpperCase() || '—'}
      />
      <Column
        header={t('common.fileTable.size')}
        headerStyle={{ width: '8rem' }}
        body={(r: AttachmentTableRow) => formatBytes(r.size)}
      />
      {showUploader && (
        <Column
          header={t('common.fileTable.uploadedBy')}
          headerStyle={{ width: '12rem' }}
          body={(r: AttachmentTableRow) => r.uploadedByName || '—'}
        />
      )}
      {showUploadedAt && (
        <Column
          header={t('common.fileTable.uploadedAt')}
          headerStyle={{ width: '12rem' }}
          body={(r: AttachmentTableRow) => (r.uploadedAt ? formatDateTime(r.uploadedAt) : '—')}
        />
      )}
      {showActions && (
        <Column
          header=""
          /* Önizleme ikonu çıkabilen tabloda satır başına ÜÇ düğme olur (Görüntüle/İndir/Kaldır);
             diğerlerinde iki. Genişlik ve hiza bu yüzden koşulludur — koşulsuz verilseydi
             `onPreview` geçmeyen tüketiciler (Toplantı Detay, Tutanak Oluştur, Görev dokümanları)
             ortalı düzenlerini kaybederdi (Duo bulgusu MR !224). */
          headerStyle={{ width: canPreviewAny ? '8rem' : '6rem' }}
          bodyStyle={{ textAlign: canPreviewAny ? 'right' : 'center' }}
          body={(r: AttachmentTableRow) => (
            <div className={`act-row-actions${canPreviewAny ? ' ui-file-row-actions' : ''}`}>
              {onPreview && r.downloadId != null && previewKindOf(r.extension) != null && (
                <button
                  type="button"
                  className="minutes-remove-btn"
                  aria-label={t('common.fileTable.preview', { name: r.fileName })}
                  title={t('common.fileTable.preview', { name: r.fileName })}
                  disabled={busy}
                  onClick={() => onPreview(r)}
                >
                  <i className="pi pi-eye" aria-hidden="true" />
                </button>
              )}
              {r.downloadId != null && (
                <AttachmentDownloadButton
                  id={r.downloadId}
                  fileName={r.fileName}
                  iconOnly
                  className="minutes-remove-btn"
                  requiredGrant={requiredGrant}
                />
              )}
              {onRemove && (
                <button
                  type="button"
                  className="minutes-remove-btn"
                  // Ad DOSYA ADINI taşır (THY inceleme bulgusu, 18.09.2026): aynı tabloda her
                  // kaldırma düğmesi sabit "Sil" okusaydı ekran okuyucu/sesli kontrol kullanıcısı
                  // hangi dosyanın kalkacağını ayırt edemezdi. Emsal aynı bileşende zaten vardı —
                  // önizleme düğmesi `common.fileTable.preview` ile adı taşıyor; kaldırma unutulmuş.
                  // `title` de aynı değeri alır: fare ipucu ile okunan ad ayrışmamalı.
                  aria-label={t('common.fileTable.remove', { name: r.fileName })}
                  title={t('common.fileTable.remove', { name: r.fileName })}
                  disabled={busy}
                  onClick={() => onRemove(r)}
                >
                  <i className="pi pi-trash" aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        />
      )}
    </DataTable>
  )
}

/**
 * Eklerin dar ekran (≤`CARD_VIEW_BREAKPOINT`) karşılığı — masaüstü tablonun YERİNE çizilir.
 *
 * <p>Kolonlar karta şöyle iniyor: **Dosya Adı** başlık, **Tür · Boyut** alt satır, **Yükleyen ·
 * Yüklenme** bilgi satırı (masaüstündeki aynı koşullarla — satırlarda o veri yoksa hiç çizilmez),
 * **İndir/Sil** eylem alanı. Kolon görünürlük kararları (`showActions`/`showUploader`/`showUploadedAt`)
 * çağıran tablodan geçirilir; iki dalın aynı koşulu iki kez hesaplaması ayrışmaya açık olurdu.
 *
 * <p>Kart TIKLANABİLİR DEĞİL: masaüstünde de satır tıklaması yok, dosya yalnız İndir ile açılır. Bu
 * yüzden `ui-card--clickable`/`role="button"` ve `isCardBodyClick` koruması gerekmez.
 *
 * <p>Bileşen `AttachmentTable` ile aynı dosyada: ikisi tek bir yüzeyin iki dalıdır ve kolon kararları
 * yukarıda hesaplanıp buraya iniyor — ayrı dosyaya alınsaydı o sözleşme görünmez olurdu.
 */
function AttachmentCards({
  rows,
  showActions,
  showUploader,
  showUploadedAt,
  onRemove,
  onPreview,
  busy,
  requiredGrant,
}: Readonly<{
  rows: AttachmentTableRow[]
  showActions: boolean
  showUploader: boolean
  showUploadedAt: boolean
  onRemove?: (row: AttachmentTableRow) => void
  onPreview?: (row: AttachmentTableRow) => void
  busy?: boolean
  requiredGrant: GrantCode
}>) {
  const { t } = useTranslation()
  return (
    <div className="ui-card-list attachment-cards">
      {rows.map((r) => (
        <div key={r.key} className="ui-card">
          <div className="ui-card-head">
            <span className="ui-card-title">{r.fileName}</span>
          </div>
          <span className="ui-card-sub">
            {[(r.extension || '').toUpperCase() || '—', formatBytes(r.size)].join(' · ')}
          </span>
          {(showUploader || showUploadedAt) && (
            <div className="ui-card-row">
              {showUploader && <span>{r.uploadedByName || '—'}</span>}
              {showUploadedAt && <span>{r.uploadedAt ? formatDateTime(r.uploadedAt) : '—'}</span>}
            </div>
          )}
          {showActions && (
            <div className="ui-card-actions">
              {onPreview && r.downloadId != null && previewKindOf(r.extension) != null && (
                <button
                  type="button"
                  className="minutes-remove-btn"
                  aria-label={t('common.fileTable.preview', { name: r.fileName })}
                  title={t('common.fileTable.preview', { name: r.fileName })}
                  disabled={busy}
                  onClick={() => onPreview(r)}
                >
                  <i className="pi pi-eye" aria-hidden="true" />
                </button>
              )}
              {r.downloadId != null && (
                <AttachmentDownloadButton
                  id={r.downloadId}
                  fileName={r.fileName}
                  iconOnly
                  className="minutes-remove-btn"
                  requiredGrant={requiredGrant}
                />
              )}
              {onRemove && (
                <button
                  type="button"
                  className="minutes-remove-btn"
                  // Ad DOSYA ADINI taşır (THY inceleme bulgusu, 18.09.2026): aynı tabloda her
                  // kaldırma düğmesi sabit "Sil" okusaydı ekran okuyucu/sesli kontrol kullanıcısı
                  // hangi dosyanın kalkacağını ayırt edemezdi. Emsal aynı bileşende zaten vardı —
                  // önizleme düğmesi `common.fileTable.preview` ile adı taşıyor; kaldırma unutulmuş.
                  // `title` de aynı değeri alır: fare ipucu ile okunan ad ayrışmamalı.
                  aria-label={t('common.fileTable.remove', { name: r.fileName })}
                  title={t('common.fileTable.remove', { name: r.fileName })}
                  disabled={busy}
                  onClick={() => onRemove(r)}
                >
                  <i className="pi pi-trash" aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
